/**
 * 41 diecezji/archidiecezji obrządku łacińskiego w Polsce — przepisane 1:1
 * z backendowego enuma `pl.iskk.dashboardbackend.attendance.Diocese`.
 * Pomija ordynariat polowy i eparchie wschodnie (backend też je pomija —
 * nie są objęte statystyką dominicantes/communicantes).
 *
 * `dioceseCode` z API (`GET /api/stats/communicantes-dominicantes/{year}/
 * dioceses`) jest walidowany Zodem właśnie względem tej listy
 * (`z.enum(DIOCESE_CODES)` w `api.ts`) — nowa wartość spoza tego zbioru
 * rzuci błędem walidacji zamiast cicho się zgubić. Jeśli backend kiedyś
 * doda/zmieni diecezję, ten plik trzeba zaktualizować w parze z enumem.
 */
export const DIOCESE_CODES = [
  'BIALOSTOCKA',
  'CZESTOCHOWSKA',
  'GDANSKA',
  'GNIEZNIENSKA',
  'KATOWICKA',
  'KRAKOWSKA',
  'LUBELSKA',
  'LODZKA',
  'POZNANSKA',
  'PRZEMYSKA',
  'SZCZECINSKO_KAMIENSKA',
  'WARMINSKA',
  'WARSZAWSKA',
  'WROCLAWSKA',
  'BIELSKO_ZYWIECKA',
  'BYDGOSKA',
  'DROHICZYNSKA',
  'ELBLASKA',
  'ELCKA',
  'GLIWICKA',
  'KALISKA',
  'KIELECKA',
  'KOSZALINSKO_KOLOBRZESKA',
  'LEGNICKA',
  'LOMZYNSKA',
  'LOWICKA',
  'OPOLSKA',
  'PELPLINSKA',
  'PLOCKA',
  'RADOMSKA',
  'RZESZOWSKA',
  'SANDOMIERSKA',
  'SIEDLECKA',
  'SOSNOWIECKA',
  'SWIDNICKA',
  'TARNOWSKA',
  'TORUNSKA',
  'WARSZAWSKO_PRASKA',
  'WLOCLAWSKA',
  'ZAMOJSKO_LUBACZOWSKA',
  'ZIELONOGORSKO_GORZOWSKA',
] as const;

export type DioceseCode = (typeof DIOCESE_CODES)[number];

export const DIOCESE_OFFICIAL_NAMES: Record<DioceseCode, string> = {
  BIALOSTOCKA: 'Archidiecezja białostocka',
  CZESTOCHOWSKA: 'Archidiecezja częstochowska',
  GDANSKA: 'Archidiecezja gdańska',
  GNIEZNIENSKA: 'Archidiecezja gnieźnieńska',
  KATOWICKA: 'Archidiecezja katowicka',
  KRAKOWSKA: 'Archidiecezja krakowska',
  LUBELSKA: 'Archidiecezja lubelska',
  LODZKA: 'Archidiecezja łódzka',
  POZNANSKA: 'Archidiecezja poznańska',
  PRZEMYSKA: 'Archidiecezja przemyska',
  SZCZECINSKO_KAMIENSKA: 'Archidiecezja szczecińsko-kamieńska',
  WARMINSKA: 'Archidiecezja warmińska',
  WARSZAWSKA: 'Archidiecezja warszawska',
  WROCLAWSKA: 'Archidiecezja wrocławska',
  BIELSKO_ZYWIECKA: 'Diecezja bielsko-żywiecka',
  BYDGOSKA: 'Diecezja bydgoska',
  DROHICZYNSKA: 'Diecezja drohiczyńska',
  ELBLASKA: 'Diecezja elbląska',
  ELCKA: 'Diecezja ełcka',
  GLIWICKA: 'Diecezja gliwicka',
  KALISKA: 'Diecezja kaliska',
  KIELECKA: 'Diecezja kielecka',
  KOSZALINSKO_KOLOBRZESKA: 'Diecezja koszalińsko-kołobrzeska',
  LEGNICKA: 'Diecezja legnicka',
  LOMZYNSKA: 'Diecezja łomżyńska',
  LOWICKA: 'Diecezja łowicka',
  OPOLSKA: 'Diecezja opolska',
  PELPLINSKA: 'Diecezja pelplińska',
  PLOCKA: 'Diecezja płocka',
  RADOMSKA: 'Diecezja radomska',
  RZESZOWSKA: 'Diecezja rzeszowska',
  SANDOMIERSKA: 'Diecezja sandomierska',
  SIEDLECKA: 'Diecezja siedlecka',
  SOSNOWIECKA: 'Diecezja sosnowiecka',
  SWIDNICKA: 'Diecezja świdnicka',
  TARNOWSKA: 'Diecezja tarnowska',
  TORUNSKA: 'Diecezja toruńska',
  WARSZAWSKO_PRASKA: 'Diecezja warszawsko-praska',
  WLOCLAWSKA: 'Diecezja włocławska',
  ZAMOJSKO_LUBACZOWSKA: 'Diecezja zamojsko-lubaczowska',
  ZIELONOGORSKO_GORZOWSKA: 'Diecezja zielonogórsko-gorzowska',
};

/**
 * Krótka nazwa bez prefiksu „Archidiecezja”/„Diecezja” — do rankingów
 * i tabel, gdzie pełna nazwa jest zbyt długa (patrz screeny: „gdańska”,
 * „tarnowska”, nie „Archidiecezja gdańska”).
 */
export function shortDioceseName(code: DioceseCode): string {
  return DIOCESE_OFFICIAL_NAMES[code].replace(/^(archidiecezja|diecezja)\s+/i, '');
}

/**
 * Slug do URL-i (`/diecezja/$slug`) — kod enuma z podkreślnikami
 * zamienionymi na myślniki, np. `BIELSKO_ZYWIECKA` → `bielsko-zywiecka`.
 */
export function dioceseSlug(code: DioceseCode): string {
  return code.toLowerCase().replace(/_/g, '-');
}

const SLUG_TO_CODE: Record<string, DioceseCode> = Object.fromEntries(
  DIOCESE_CODES.map((code) => [dioceseSlug(code), code]),
);

/** Odwrotność `dioceseSlug()` — `null` dla slugu spoza 41 diecezji. */
export function dioceseCodeFromSlug(slug: string): DioceseCode | null {
  return SLUG_TO_CODE[slug] ?? null;
}

/**
 * Normalizacja do dopasowania wolnego stringu z API do `DioceseCode`:
 * bez diakrytyków, lowercase, bez prefiksu „archidiecezja/diecezja”, bez
 * nadmiarowych spacji.
 */
function normalizeDioceseName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/^(archi)?diecezja\s+/, '')
    .trim();
}

const NORMALIZED_NAME_TO_CODE: Record<string, DioceseCode> = Object.fromEntries(
  DIOCESE_CODES.map((code) => [
    normalizeDioceseName(DIOCESE_OFFICIAL_NAMES[code]),
    code,
  ]),
);

const NORMALIZED_CODE_TO_CODE: Record<string, DioceseCode> = Object.fromEntries(
  DIOCESE_CODES.map((code) => [normalizeDioceseName(code.replace(/_/g, ' ')), code]),
);

/**
 * Wolny string `diocese`/`deanery.diocese` z nowego API → `DioceseCode`,
 * albo `null`, gdy nic nie pasuje. Backend nie ogranicza już tego pola do
 * enuma (patrz CLAUDE.md, sekcja Backend) — nie wiadomo dziś na pewno, czy
 * wyśle pełną nazwę urzędową („Diecezja tarnowska”) czy sam kod enuma
 * („TARNOWSKA”), więc próbujemy obu dopasowań po tej samej normalizacji.
 * Celowo NIE rzuca błędu przy braku dopasowania — wywołujący ma łagodnie
 * pominąć/nie pokolorować regionu, nie wywalić całego widoku. Do
 * zweryfikowania na żywych danych po pierwszym imporcie.
 */
export function matchDioceseCode(apiName: string): DioceseCode | null {
  const normalized = normalizeDioceseName(apiName);
  return (
    NORMALIZED_NAME_TO_CODE[normalized] ?? NORMALIZED_CODE_TO_CODE[normalized] ?? null
  );
}

/**
 * Liczba dekanatów i parafii — dane strukturalne ze schematyzmu, stabilne
 * (zmieniają się rzadko, nie są statystyką roczną jak dominicantes), więc
 * NIE pochodzą z `dashboardApiUrl` (backend ich w ogóle nie wystawia — patrz
 * `/v3/api-docs`). Przepisane 1:1 z prototypu (`STATIC_DATA.deaneries` /
 * `STATIC_DATA.parishes`, zliczone per `diocese_slug`) — zweryfikowane
 * programowo względem wszystkich 41 slugów z `DIOCESE_CODES`, zero
 * rozbieżności. Ordynariat polowy (obecny w danych źródłowych) pominięty,
 * tak jak w całym module — nie jest objęty statystyką dominicantes/
 * communicantes.
 */
export const DIOCESE_STRUCTURE: Record<
  DioceseCode,
  { deaneries: number; parishes: number }
> = {
  BIALOSTOCKA: { deaneries: 13, parishes: 116 },
  CZESTOCHOWSKA: { deaneries: 35, parishes: 312 },
  GDANSKA: { deaneries: 24, parishes: 198 },
  GNIEZNIENSKA: { deaneries: 30, parishes: 266 },
  KATOWICKA: { deaneries: 37, parishes: 321 },
  KRAKOWSKA: { deaneries: 46, parishes: 448 },
  LUBELSKA: { deaneries: 28, parishes: 271 },
  LODZKA: { deaneries: 28, parishes: 219 },
  POZNANSKA: { deaneries: 43, parishes: 415 },
  PRZEMYSKA: { deaneries: 41, parishes: 391 },
  SZCZECINSKO_KAMIENSKA: { deaneries: 36, parishes: 273 },
  WARMINSKA: { deaneries: 33, parishes: 263 },
  WARSZAWSKA: { deaneries: 25, parishes: 211 },
  WROCLAWSKA: { deaneries: 33, parishes: 297 },
  BIELSKO_ZYWIECKA: { deaneries: 23, parishes: 210 },
  BYDGOSKA: { deaneries: 21, parishes: 149 },
  DROHICZYNSKA: { deaneries: 11, parishes: 97 },
  ELBLASKA: { deaneries: 21, parishes: 157 },
  ELCKA: { deaneries: 18, parishes: 152 },
  GLIWICKA: { deaneries: 16, parishes: 156 },
  KALISKA: { deaneries: 33, parishes: 283 },
  KIELECKA: { deaneries: 33, parishes: 303 },
  KOSZALINSKO_KOLOBRZESKA: { deaneries: 24, parishes: 220 },
  LEGNICKA: { deaneries: 26, parishes: 238 },
  LOMZYNSKA: { deaneries: 24, parishes: 182 },
  LOWICKA: { deaneries: 21, parishes: 166 },
  OPOLSKA: { deaneries: 36, parishes: 396 },
  PELPLINSKA: { deaneries: 30, parishes: 292 },
  PLOCKA: { deaneries: 28, parishes: 247 },
  RADOMSKA: { deaneries: 29, parishes: 301 },
  RZESZOWSKA: { deaneries: 25, parishes: 243 },
  SANDOMIERSKA: { deaneries: 25, parishes: 242 },
  SIEDLECKA: { deaneries: 25, parishes: 249 },
  SOSNOWIECKA: { deaneries: 23, parishes: 162 },
  SWIDNICKA: { deaneries: 24, parishes: 192 },
  TARNOWSKA: { deaneries: 43, parishes: 459 },
  TORUNSKA: { deaneries: 24, parishes: 196 },
  WARSZAWSKO_PRASKA: { deaneries: 21, parishes: 183 },
  WLOCLAWSKA: { deaneries: 33, parishes: 233 },
  ZAMOJSKO_LUBACZOWSKA: { deaneries: 19, parishes: 187 },
  ZIELONOGORSKO_GORZOWSKA: { deaneries: 29, parishes: 270 },
};
